/**
 * Security utilities for sanitizing and protecting sensitive information
 */

/**
 * Sanitize error message to prevent information disclosure
 * @param {string|Error} input - Error message or Error object
 * @returns {string} - Sanitized error message
 */
export function sanitizeErrorMessage(input) {
  let message = '';

  if (!input) {
    return 'An error occurred';
  }

  if (typeof input === 'string') {
    message = input;
  } else if (input instanceof Error) {
    message = input.message || 'An error occurred';
  } else {
    message = String(input);
  }

  // Patterns that might contain sensitive information
  const sensitivePatterns = [
    // Authentication tokens
    /Bearer\s+[A-Za-z0-9\-._~+/]+=*/gi,
    /([a-zA-Z0-9]{32,})/g, // API keys
    /([a-zA-Z0-9+/]{40,}={0,2})/g, // Base64 encoded strings

    // Connection strings
    /mongodb:\/\/[^@]+@[^/]+/gi,
    /postgres:\/\/[^@]+@[^/]+/gi,
    /DefaultEndpointsProtocol[^;]+;[^;]+/gi,

    // Credentials
    /password["\s:=]+["']?[^\s,}]+/gi,
    /pwd["\s:=]+["']?[^\s,}]+/gi,
    /secret["\s:=]+["']?[^\s,}]+/gi,
    /key["\s:=]+["']?[^\s,}]+/gi,
    /token["\s:=]+["']?[^\s,}]+/gi,
    /apikey["\s:=]+["']?[^\s,}]+/gi,

    // URLs with credentials
    /\/\/[^:]+:[^@]+@/g,

    // IP addresses (internal)
    /(10\.\d{1,3}\.\d{1,3}\.\d{1,3})/g,
    /(172\.(1[6-9]|2[0-9]|3[01])\.\d{1,3}\.\d{1,3})/g,
    /(192\.168\.\d{1,3}\.\d{1,3})/g,

    // File paths that might reveal structure
    /[cC]:\\[^\\]+\\/g,
    /\/home\/[^/]+/g,
    /\/Users\/[^/]+/g,

    // Azure/AWS specific - patterns intentionally match substrings within URLs to redact endpoints
    // lgtm[js/regex/missing-regexp-anchor]
    // codeql[js/regex/missing-regexp-anchor]: Substrings are intentional - anchors prevent embedded hostname matches
    /[a-z0-9-]+\.blob\.core\.windows\.net\b/gi,
    // lgtm[js/regex/missing-regexp-anchor]
    // codeql[js/regex/missing-regexp-anchor]: Substrings are intentional - anchors prevent embedded hostname matches
    /[a-z0-9-]+\.amazonaws\.com\b/gi,
    /AccountName=[^;]+/gi,
    /AccountKey=[^;]+/gi
  ];

  let sanitized = message;

  // Replace sensitive patterns with generic placeholders
  sensitivePatterns.forEach(pattern => {
    sanitized = sanitized.replace(pattern, '[REDACTED]');
  });

  // Truncate overly long messages
  if (sanitized.length > 500) {
    sanitized = sanitized.substring(0, 497) + '...';
  }

  return sanitized;
}

/**
 * Sanitize object for logging by removing sensitive fields
 * @param {Object} obj - Object to sanitize
 * @param {Array} sensitiveFields - Additional field names to redact
 * @returns {Object} - Sanitized object copy
 */
export function sanitizeObject(obj, sensitiveFields = []) {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  const defaultSensitiveFields = [
    'password',
    'pwd',
    'secret',
    'token',
    'apiKey',
    'apiSecret',
    'clientSecret',
    'privateKey',
    'connectionString',
    'authorization',
    'cookie',
    'session',
    'credential',
    'accessToken',
    'refreshToken'
  ];

  const allSensitiveFields = [...defaultSensitiveFields, ...sensitiveFields].map(f => f.toLowerCase());

  const sanitized = Array.isArray(obj) ? [] : {};

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const lowerKey = key.toLowerCase();

      // Check if field is sensitive
      if (allSensitiveFields.some(field => lowerKey.includes(field))) {
        sanitized[key] = '[REDACTED]';
      } else if (typeof obj[key] === 'object' && obj[key] !== null) {
        // Recursively sanitize nested objects
        sanitized[key] = sanitizeObject(obj[key], sensitiveFields);
      } else if (typeof obj[key] === 'string') {
        // Sanitize string values
        sanitized[key] = sanitizeErrorMessage(obj[key]);
      } else {
        sanitized[key] = obj[key];
      }
    }
  }

  return sanitized;
}

/**
 * Create a safe error for external exposure
 * @param {Error} error - Original error
 * @param {string} context - Context where error occurred
 * @returns {Object} - Safe error object
 */
export function createSafeError(error, context = '') {
  const safeError = {
    message: 'An error occurred processing your request',
    context: context || undefined,
    timestamp: new Date().toISOString()
  };

  // Only include sanitized details in development
  if (process.env.NODE_ENV === 'development') {
    safeError.details = sanitizeErrorMessage(error.message);
    safeError.type = error.name || 'Error';
  }

  // Add request ID if available
  if (error.requestId) {
    safeError.requestId = error.requestId;
  }

  // Add retry information if available
  if (error.retryable) {
    safeError.retryable = true;
    safeError.retryAfter = error.retryAfter || undefined;
  }

  return safeError;
}

/**
 * Mask sensitive parts of a string
 * @param {string} value - Value to mask
 * @param {number} visibleChars - Number of characters to keep visible at start
 * @returns {string} - Masked value
 */
export function maskSensitiveValue(value, visibleChars = 4) {
  if (!value || typeof value !== 'string') {
    return '[INVALID]';
  }

  if (value.length <= visibleChars) {
    return '****';
  }

  const visible = value.substring(0, visibleChars);
  const masked = '*'.repeat(Math.min(value.length - visibleChars, 20));

  return `${visible}${masked}`;
}

/**
 * Validate and sanitize URL
 * @param {string} url - URL to validate
 * @returns {string} - Sanitized URL or null if invalid
 */
export function sanitizeUrl(url) {
  if (!url || typeof url !== 'string') {
    return null;
  }

  try {
    const parsed = new URL(url);

    // Remove credentials from URL
    parsed.username = '';
    parsed.password = '';

    // Remove sensitive query parameters
    const sensitiveParams = ['token', 'key', 'secret', 'password', 'apikey'];
    sensitiveParams.forEach(param => {
      parsed.searchParams.delete(param);
    });

    return parsed.toString();
  } catch {
    return null;
  }
}

const securityUtils = {
  sanitizeErrorMessage,
  sanitizeObject,
  createSafeError,
  maskSensitiveValue,
  sanitizeUrl
};

export default securityUtils;
